import { Component, inject, computed, input, output, signal } from '@angular/core';
import { Router } from '@angular/router';

import { IconLibrary, SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { LanguageService } from '@fcom/ui-translate';
import { GlobalBookingTravelClass } from '@fcom/core';
import { CampaignTagToLabelKey } from '@fcom/core/constants';
import { AspectRatioString, FinnairThemeService } from '@fcom/common';
import { TripType } from '@fcom/core/interfaces/triptype.interface';
import { ButtonMode, ButtonSize, ButtonTheme } from '@fcom/ui-components/components/buttons/button/enums';
import { IconButtonTheme } from '@fcom/ui-components/components/buttons/icon-button/enums';
import { IconPosition } from '@fcom/ui-components/components/icons/enums';
import { FetchPriorityEnum } from '@fcom/ui-components/components/images/enums';
import { TagRoundingStyle, TagTheme } from '@fcom/ui-components/components/tag/interfaces';
import { isPresent } from '@fcom/core/utils/utils';
import { CmsTripTypeMap } from '@fcom/core/utils/tripType.utils';
import { Amount } from '@fcom/dapi';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';

import { BlockOffer } from '../../interfaces/destination-promo.interface';
import { MarketingOfferPrices } from '../../interfaces/marketing-offer.interface';

@Component({
  selector: 'fin-block-offer-tile',
  templateUrl: './block-offer-tile.component.html',
  styleUrls: ['./block-offer-tile.component.scss'],
  standalone: false,
})
export class BlockOfferTileComponent {
  private router = inject(Router);
  private languageService = inject(LanguageService);
  private themeService = inject(FinnairThemeService);

  readonly CmsTripTypeMap = CmsTripTypeMap;
  readonly GlobalBookingTravelClass = GlobalBookingTravelClass;
  readonly MaxImageWidth = 600;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TripType = TripType;
  readonly ButtonTheme = ButtonTheme;
  readonly IconButtonTheme = IconButtonTheme;
  readonly IconPosition = IconPosition;
  readonly IconLibrary = IconLibrary;
  readonly ButtonSize = ButtonSize;
  readonly ButtonMode = ButtonMode;
  readonly TagTheme = TagTheme;
  readonly TagRoundingStyle = TagRoundingStyle;

  readonly isNewBrand = computed(() => this.themeService.siteTheme() !== FinnairTheme.LEGACY);

  linkClicked = output<[GlobalBookingTravelClass, TripType]>();
  amContinueModalClick = output<{ amLink: string; destinationTitle: string; destinationCode: string }>();
  calendarClicked = output<[Event, GlobalBookingTravelClass, TripType]>();

  tripType = input<TripType>();
  blockOffer = input<BlockOffer>();
  showRoundTripPrice = input(false);
  fetchImagePriority = input<FetchPriorityEnum>();
  showPriceCalendarButton = input(false);
  aspectRatio = input<AspectRatioString>('16x9');

  readonly innerLinkHover = signal(false);
  readonly flightHotelEnabled = this.languageService.localeValue.includes('FI');

  private readonly currencyCode = this.languageService.translateSignal('number.currency');

  readonly resolvedTripType = computed<TripType>(() => {
    if (isPresent(this.tripType())) {
      return this.tripType();
    }
    if (this.showRoundTripPrice() || !this.blockOffer()?.isShortHaul) {
      return TripType.RETURN;
    }
    return TripType.ONEWAY;
  });

  readonly discountCodeFixedAmount = computed<Amount>(() => {
    const discountCode = this.blockOffer()?.discountCode;
    if (discountCode?.type !== 'fixed') {
      return undefined;
    }
    return {
      amount: discountCode.amount,
      currencyCode: this.currencyCode(),
    };
  });

  readonly badgeLabel = computed<string>(() => {
    const badgeLabel = this.blockOffer()?.badgeLabel;
    const discountCode = this.blockOffer()?.discountCode;
    if (!isPresent(badgeLabel)) {
      return undefined;
    }
    if (badgeLabel !== CampaignTagToLabelKey.fcomCampaignCode || !isPresent(discountCode?.type)) {
      return `destinationSearch.${badgeLabel}`;
    }
    if (discountCode.type === 'fixed') {
      return 'destinationSearch.discountCodeFixed';
    }
    return 'destinationSearch.discountCodePercentage';
  });

  readonly isHighlightPrice = computed<boolean>(() => {
    const badgeLabel = this.blockOffer()?.badgeLabel;
    return (
      isPresent(badgeLabel) &&
      [CampaignTagToLabelKey.fcomCampaignSpecial, CampaignTagToLabelKey.fcomCampaignFlash].includes(
        badgeLabel as CampaignTagToLabelKey
      )
    );
  });

  readonly priceContext = computed(() => {
    const blockOffer = this.blockOffer();
    if (!blockOffer) {
      return undefined;
    }
    return this.getPriceContext(blockOffer, this.showRoundTripPrice());
  });

  emitClick(travelClass: GlobalBookingTravelClass, tripType?: TripType): void {
    if (this.showRoundTripPrice()) {
      this.linkClicked.emit([travelClass || GlobalBookingTravelClass.MIXED, TripType.RETURN]);
    } else {
      this.linkClicked.emit([travelClass || GlobalBookingTravelClass.MIXED, tripType]);
    }
  }

  emitCalendarClick(event: Event, travelClass: GlobalBookingTravelClass, tripType?: TripType): void {
    this.calendarClicked.emit([event, travelClass || GlobalBookingTravelClass.MIXED, tripType]);
  }

  openAmContinueModalClick(amLink: string, destinationTitle: string, destinationCode: string): void {
    this.amContinueModalClick.emit({ amLink, destinationTitle, destinationCode });
  }

  navigateToUrl(link: string): void {
    this.router.navigateByUrl(link);
  }

  getPriceContext(
    offer: BlockOffer,
    showRoundTripPrices?: boolean
  ): { price: MarketingOfferPrices; onewayPrice: MarketingOfferPrices; returnPrice: MarketingOfferPrices } {
    if (showRoundTripPrices) {
      return {
        price: offer.returnPrice,
        onewayPrice: offer?.onewayPrice,
        returnPrice: offer?.returnPrice,
      };
    }

    return isPresent(this.tripType())
      ? {
          price: this.tripType() === TripType.ONEWAY ? offer.onewayPrice : offer.returnPrice,
          onewayPrice: offer?.onewayPrice,
          returnPrice: offer?.returnPrice,
        }
      : {
          price: offer.isShortHaul ? offer.onewayPrice : offer.returnPrice,
          onewayPrice: offer?.onewayPrice,
          returnPrice: offer?.returnPrice,
        };
  }
}
